import React from 'react';
import { useIntl } from '../../util/reactIntl';
import logo from '../../assets/logo.webp';

const toText = value => (value == null ? '' : String(value));

const toAddressLine = (address, key) => toText(address?.[key]);

const ComponentToPrint = props => {
  const intl = useIntl();
  const { providerName, orderId, startDate, address = {}, buyerName, savedCart = [] } = props;

  const line1 = toAddressLine(address, 'line1');
  const line2 = toAddressLine(address, 'line2');
  const city = toAddressLine(address, 'city');
  const state = toAddressLine(address, 'state');
  const postalCode = toAddressLine(address, 'postalCode');
  const country = toAddressLine(address, 'country');
  const cityStatePostal = [city, state, postalCode].filter(Boolean).join(', ');
  const items = Array.isArray(savedCart) ? savedCart : [];

  return (
    <div>
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="Generator" content="Microsoft Word 15 (filtered)" />
      <style
        // NOTE: Keep this embedded style block unchanged to preserve the existing slip design.
        dangerouslySetInnerHTML={{
          __html:
            '\n<!--\n /* Font Definitions */\n @font-face\n\t{font-family:"Cambria Math";\n\tpanose-1:2 4 5 3 5 4 6 3 2 4;}\n@font-face\n\t{font-family:Montserrat;}\n /* Style Definitions */\n p.MsoNormal, li.MsoNormal, div.MsoNormal\n\t{margin:0in;\n\tline-height:115%;\n\tfont-size:11.0pt;\n\tfont-family:"Arial",sans-serif;}\n.MsoChpDefault\n\t{font-size:10.0pt;\n\tfont-family:"Arial",sans-serif;}\n /* Page Definitions */\n @page WordSection1\n\t{size:8.5in 11.0in;\n\tmargin:1.0in 1.0in 1.0in 1.0in;}\ndiv.WordSection1\n\t{page:WordSection1;}\n-->\n',
        }}
      />
      <div className="WordSection1">
        <p className="MsoNormal" align="center">
          <b>
            <span>
              {intl.formatMessage(
                { id: 'TransactionPage.packingSlip.thankYouOrderWith' },
                { providerName: toText(providerName) }
              )}
            </span>
          </b>
        </p>
        <p className="MsoNormal">
          <span>&nbsp;</span>
        </p>
        <p className="MsoNormal" align="right" style={{ textAlign: 'right' }}>
          <span>{intl.formatMessage({ id: 'TransactionPage.packingSlip.orderIdLabel' })} </span>
          <span>{toText(orderId)}</span>
        </p>
        <p className="MsoNormal" align="right" style={{ textAlign: 'right' }}>
          <span>
            {intl.formatMessage({ id: 'TransactionPage.packingSlip.dateLabel' })} {toText(startDate)}
          </span>
        </p>
        <p className="MsoNormal">
          <span>&nbsp;</span>
        </p>
        <p className="MsoNormal">
          <b>
            <span>{intl.formatMessage({ id: 'TransactionPage.packingSlip.shipTo' })} </span>
          </b>
        </p>
        <p className="MsoNormal">
          <span>{toText(buyerName)}</span>
        </p>
        <p className="MsoNormal">
          <span>{line1}</span>
        </p>
        <p className="MsoNormal">
          <span>{line2}</span>
        </p>
        <p className="MsoNormal">
          <span>{cityStatePostal}</span>
        </p>
        <p className="MsoNormal">
          <span>{country}</span>
        </p>
        <p className="MsoNormal">
          <b>
            <span>&nbsp;</span>
          </b>
        </p>
        <div className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <span>
            <hr style={{ opacity: '0.1' }} size={1} width="100%" align="center" />
          </span>
        </div>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th className="MsoNormal" style={{ textAlign: 'left', fontFamily: '"Arial",sans-serif' }}>
                {intl.formatMessage({ id: 'TransactionPage.packingSlip.itemLabel' })}
              </th>
              <th className="MsoNormal" style={{ textAlign: 'center', fontFamily: '"Arial",sans-serif' }}>
                {intl.formatMessage({ id: 'TransactionPage.packingSlip.quantityLabel' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => {
                const itemTitle = toText(item?.title);
                const brand = toText(item?.brand);
                const sku = toText(item?.sku);
                const size = toText(item?.size);
                const quantity = item?.quantity || 1;
                const imageSrc = toText(item?.firstImage);

                return (
                  <tr key={`${itemTitle}-${index}`}>
                    <td
                      className="MsoNormal"
                      style={{
                        padding: '5px 0',
                        borderTop: 'solid 1px #ddd',
                        fontFamily: '"Arial",sans-serif',
                      }}
                    >
                      <table>
                        <tbody>
                          <tr>
                            <td>
                              {imageSrc ? (
                                <img
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '4px',
                                    marginRight: '10px',
                                  }}
                                  src={imageSrc}
                                  alt={
                                    itemTitle ||
                                    intl.formatMessage({
                                      id: 'TransactionPage.packingSlip.productImageAlt',
                                    })
                                  }
                                />
                              ) : null}
                            </td>
                            <td>
                              <p
                                style={{
                                  margin: '0 0 5px 0',
                                  lineHeight: '100%',
                                  fontFamily: '"Arial",sans-serif',
                                  fontSize: '12px',
                                }}
                              >
                                {itemTitle}
                              </p>
                              {brand ? (
                                <p
                                  style={{
                                    margin: '0 0 5px 0',
                                    lineHeight: '100%',
                                    fontFamily: '"Arial",sans-serif',
                                    fontSize: '12px',
                                  }}
                                >
                                  {intl.formatMessage({
                                    id: 'TransactionPage.packingSlip.brandLabel',
                                  })}{' '}
                                  {brand}
                                </p>
                              ) : null}
                              {sku ? (
                                <p
                                  style={{
                                    margin: '0',
                                    lineHeight: '100%',
                                    fontFamily: '"Arial",sans-serif',
                                    fontSize: '12px',
                                  }}
                                >
                                  {intl.formatMessage({ id: 'TransactionPage.packingSlip.skuLabel' })}{' '}
                                  {sku}
                                </p>
                              ) : null}
                              {size ? (
                                <p
                                  style={{
                                    margin: '4px 0 0 0',
                                    lineHeight: '100%',
                                    fontFamily: '"Arial",sans-serif',
                                    fontSize: '12px',
                                  }}
                                >
                                  {intl.formatMessage({ id: 'TransactionPage.packingSlip.sizeLabel' })}{' '}
                                  {size}
                                </p>
                              ) : null}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td
                      className="MsoNormal"
                      style={{
                        textAlign: 'center',
                        padding: '5px 0',
                        borderTop: 'solid 1px #ddd',
                        fontFamily: '"Arial",sans-serif',
                      }}
                    >
                      <span style={{ fontFamily: '"Arial",sans-serif', fontSize: '12px' }}>
                        {quantity}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  className="MsoNormal"
                  style={{
                    padding: '5px 0',
                    borderTop: 'solid 1px #ddd',
                    fontFamily: '"Arial",sans-serif',
                  }}
                >
                  {intl.formatMessage({
                    id: 'TransactionPage.packingSlip.itemDetailsUnavailable',
                  })}
                </td>
                <td
                  className="MsoNormal"
                  style={{
                    textAlign: 'center',
                    padding: '5px 0',
                    borderTop: 'solid 1px #ddd',
                    fontFamily: '"Arial",sans-serif',
                  }}
                >
                  <span style={{ fontFamily: '"Arial",sans-serif', fontSize: '12px' }}>1</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <span>
            <hr style={{ opacity: '0.1' }} size={1} width="100%" align="center" />
          </span>
        </div>
        <p className="MsoNormal">
          <b>
            <span>&nbsp;</span>
          </b>
        </p>
        <p className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <b>
            <span>{intl.formatMessage({ id: 'TransactionPage.packingSlip.thankYouMessage' })}</span>
          </b>
        </p>
        <p className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <b>
            <span>&nbsp;</span>
          </b>
        </p>
        <p className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <span>
            <img
              width={150}
              height={35}
              id="image1.png"
              src={logo}
              alt={intl.formatMessage({ id: 'TransactionPage.packingSlip.logoAlt' })}
            />
          </span>
        </p>
        <p className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <span>
            <a href="http://www.tutulist.com">
              <span style={{ color: 'windowtext', textDecoration: 'none' }}>www.tutulist.com</span>
            </a>
          </span>
        </p>
        <p className="MsoNormal" align="center" style={{ textAlign: 'center' }}>
          <span>&nbsp;</span>
        </p>
      </div>
    </div>
  );
};

export default ComponentToPrint;
